// useSSE — subscribes to a run's live event stream (FD §22.4) via
// EventSource and feeds parsed RunEvents to the caller (the Run detail
// page's Timeline/Conversation/Tool calls/Terminal tabs all derive their
// view from the same `events` array).
//
// Reconnection: the browser's EventSource retries automatically on a
// dropped connection, but per routes/events/sse.ts's contract we must
// resume from the last event id we saw (`?lastEventId=`) so the server can
// replay anything missed while disconnected, rather than starting the
// buffer over. `withCredentials`/custom headers are not an option for SSE,
// which is why auth goes over `?token=` (see api-client.ts's `sseUrl`).

import { useEffect, useRef, useState } from 'react'
import type { RunEvent } from '@goblic/shared'
import { runsApi } from '../lib/api-client'
import { probeSessionAfterStreamError } from '../lib/api-client'

export type SseConnectionStatus =
  'idle' | 'connecting' | 'open' | 'error' | 'closed'

export interface UseSSEResult {
  /** All events received so far, oldest first. */
  events: RunEvent[]
  status: SseConnectionStatus
  /** The id of the most recently received event, used for reconnect replay. */
  lastEventId: string | null
  /** Clears the locally buffered events (e.g. when switching to a different run). */
  clear: () => void
}

/**
 * Connects to `GET /runs/:runId/events` and appends every received RunEvent
 * to `events`. Pass `undefined` for `runId` to stay idle (e.g. while the run
 * itself is still loading).
 */
export function useSSE(runId: string | undefined): UseSSEResult {
  const [events, setEvents] = useState<RunEvent[]>([])
  const [status, setStatus] = useState<SseConnectionStatus>('idle')
  const lastEventIdRef = useRef<string | null>(null)
  const currentRunIdRef = useRef<string | undefined>(runId)

  useEffect(() => {
    if (!runId) {
      setStatus('idle')
      return
    }

    // Another run (e.g. following a handoff chain link on the same page):
    // start its stream from the beginning, not where the previous one was.
    if (currentRunIdRef.current !== runId) {
      currentRunIdRef.current = runId
      lastEventIdRef.current = null
      setEvents([])
    }

    setStatus('connecting')
    const source = new EventSource(
      runsApi.sseUrl(runId, lastEventIdRef.current ?? undefined)
    )

    source.onopen = () => setStatus('open')

    source.onmessage = (message: MessageEvent<string>) => {
      // TODO: once event-stream.service.ts defines named SSE event types per
      // RunEvent['type'] (source.addEventListener(type, handler)), switch to
      // that instead of parsing every message off the generic 'message'
      // channel — this is a defensive placeholder in the meantime.
      try {
        const parsed = JSON.parse(message.data) as RunEvent
        if (message.lastEventId) {
          lastEventIdRef.current = message.lastEventId
        }
        setEvents((previous) => [...previous, parsed])
      } catch {
        // TODO: surface malformed-event telemetry instead of silently
        // dropping the message.
      }
    }

    source.onerror = () => {
      // EventSource retries on its own; we just reflect connection status.
      // TODO: after N consecutive errors, stop relying on the browser's
      // built-in retry/backoff and reconnect manually so the UI can show a
      // "give up and reload" affordance.
      setStatus('error')
      probeSessionAfterStreamError()
    }

    return () => {
      source.close()
    }
  }, [runId])

  function clear(): void {
    setEvents([])
    lastEventIdRef.current = null
  }

  return { events, status, lastEventId: lastEventIdRef.current, clear }
}
